import React, { createContext, useContext, useMemo } from 'react';
import { ProgramContext } from '@plentifulapp/plentiful-program-configuration';
import StateProgramFacade from './StateProgramFacade';
import { neighborProgramContext } from './neighborProgramContext';
import { usePlentifulServiceContext } from '../services/PlentifulServiceContext';
import StateProgramsError from './errors/StateProgramsError';
import { debug, logInfo } from '../../utils/logger';

/**
 * Context provider vends services or null if there was an error.
 * Visible for testing. Avoid using directly.
 * Prefer to use {@link StateProgramContextProvider}
 * and {@link useStateProgramContext} instead.
 */
export const StateProgramContext = createContext<StateProgramFacade | null>(null);

/**
 * Custom hook to provide StateProgramServices based on context.
 * Returns null if the program was not found in the bundled configuration.
 * Consumers are responsible for handling the null case.
 */
export const useStateProgramContext = (): StateProgramFacade | null => useContext(StateProgramContext);

/**
 * Props for providing state program services.
 */
interface StateProgramContextProviderProps {
  /**
   * State program ID that is being provided. Required.
   */
  programId: string;
  /**
   * Factory method for building the state program services.
   * Typically in production should be {@link StateProgramFacade#buildForProgram}.
   */
  programServicesFactory: typeof StateProgramFacade.buildForProgram;
  /**
   * Function to check if a program ID is valid.
   * Defaults to the neighbor program context's isValidProgram.
   */
  isValidProgram?: ProgramContext['isValidProgram'];
  /**
   * Optional child nodes that will use the context.
   */
  children?: React.ReactNode;
}

/**
 * Provider to state program services.
 * Wraps built-in provider to ensure that program services
 * can be loaded before being used.
 */
export const StateProgramContextProvider = (props: StateProgramContextProviderProps) => {
  const serviceRegistry = usePlentifulServiceContext();
  let programServices: StateProgramFacade | null = null;

  const { programId, programServicesFactory, isValidProgram = neighborProgramContext.isValidProgram } = props;

  // Check if program exists in the bundled configuration
  // This can happen when the API returns a pantry opted into a program that hasn't been bundled yet
  const programExists = isValidProgram(programId);

  // TODO: # 3329 - State program configuration should load asynchronously so that pantry details rendering is not blocked
  // TODO # 3330 - Ensure memoziation of state program configuration does not cause memory overhead
  programServices = useMemo(() => {
    if (!programExists) {
      // Log for tracking, but don't throw an error
      // (This is expected behavior when the mobile app hasn't been updated with the latest program config)
      logInfo(
        'Program configuration not found in bundle; pantry opted into program not yet available in this app version',
        { missingProgramId: programId }
      );
      return null;
    }

    try {
      // The bundled config/engine dependencies default inside buildForProgram;
      // we supply only the environment-specific attestation facade.
      return programServicesFactory(programId, serviceRegistry.attestationFacade);
    } catch (cause) {
      // Throw the error immediately for the parent error boundary to catch
      // This is better than silently returning null, as it allows proper error handling
      throw new StateProgramsError('threw exception when loading program services', cause, programId);
    }
  }, [programId, programServicesFactory, serviceRegistry.attestationFacade, programExists]);

  if (programServices) {
    debug('Done loading state program configuration', { programId });
  }

  return (
    <StateProgramContext.Provider value={programServices}>{props.children}</StateProgramContext.Provider>
  );
};
